/**
 * Customer-facing Tesla-style theme configuration
 * Modern, minimalist design with premium feel
 */

import { createTheme } from '@mui/material/styles';

// Professional color palette matching admin theme
const colors = {
  primary: {
    main: '#10b981', // Professional green from admin
    light: '#34d399',
    dark: '#059669',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#161616', // Dark from admin theme
    light: '#282828',
    dark: '#0a0a0a',
    contrastText: '#ffffff',
  },
  accent: {
    main: '#3b82f6', // Professional blue
    light: '#60a5fa',
    dark: '#2563eb',
  },
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    dark: '#000000',
    darkPaper: '#111111',
  },
  text: {
    primary: '#000000',
    secondary: '#666666',
    disabled: '#999999',
    primaryDark: '#ffffff',
    secondaryDark: '#cccccc',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  }
};

// Light theme
export const customerThemeLight = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    grey: colors.grey,
  },
  
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    
    // Headings
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    
    // Body text
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    
    // Buttons
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '1rem',
    },
  },

  shape: {
    borderRadius: 8,
  },

  spacing: 8,

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          '-webkit-font-smoothing': 'antialiased',
          '-moz-osx-font-smoothing': 'grayscale',
          scrollbarWidth: 'thin',
          scrollbarColor: '#d1d1d1 transparent',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#d1d1d1',
            borderRadius: '4px',
            '&:hover': {
              background: '#c1c1c1',
            },
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: '8px',
          padding: '12px 24px',
          fontSize: '1rem',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
        contained: {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          '&:hover': {
            backgroundColor: colors.primary.light,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          },
        },
        outlined: {
          borderColor: colors.primary.main,
          color: colors.primary.main,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
            borderColor: colors.primary.main,
          },
        },
        text: {
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: '12px',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'rgba(0,0,0,0.02)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.04)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(0,0,0,0.02)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.accent.main,
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          fontWeight: 500,
          fontSize: '0.875rem',
        },
        filled: {
          backgroundColor: 'rgba(0,0,0,0.08)',
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.12)',
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          boxShadow: 'none',
          color: colors.text.primary,
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '70px !important',
          padding: '0 24px',
        },
      },
    },
  },
});

// Dark theme
export const customerThemeDark = createTheme({
  ...customerThemeLight,
  palette: {
    mode: 'dark',
    primary: {
      main: '#ffffff',
      light: '#f5f5f5',
      dark: '#cccccc',
      contrastText: '#000000',
    },
    secondary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    background: {
      default: colors.background.dark,
      paper: colors.background.darkPaper,
    },
    text: {
      primary: colors.text.primaryDark,
      secondary: colors.text.secondaryDark,
    },
    grey: colors.grey,
  },
  
  components: {
    ...customerThemeLight.components,
    
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.dark,
          color: colors.text.primaryDark,
          scrollbarColor: '#555 transparent',
          '&::-webkit-scrollbar-thumb': {
            background: '#555',
            '&:hover': {
              background: '#666',
            },
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        ...customerThemeLight.components?.MuiButton?.styleOverrides,
        contained: {
          backgroundColor: '#ffffff',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        },
        outlined: {
          borderColor: '#ffffff',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.08)',
          },
        },
        text: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.08)',
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.darkPaper,
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.3)',
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255,255,255,0.05)',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.08)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#cccccc',
          },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          color: '#ffffff',
        },
      },
    },
  },
});

export default customerThemeLight;